import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';

import { SettingsEditableValue } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useTenantLogo, useTenantName } from '@eluve/smart-blocks';

import { updateTenantDetailsMutation } from './operations';

const EditTenantNameDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const { tenantLogoUrl } = useTenantLogo(tenantId);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingName, setPendingName] = useState(tenantName ?? '');

  const [updateTenantDetails] = useMutation(updateTenantDetailsMutation, {
    onError: () => {
      toast.error(t('tenantAdmin.general.nameUpdateFailed'));
    },
    optimisticResponse: (data) => ({
      updateTenantsByPk: {
        __typename: 'Tenants' as const,
        id: tenantId,
        name: data.name ?? '',
        logoUrl: tenantLogoUrl,
      },
    }),
  });

  useEffect(() => {
    if (open) {
      setPendingName(tenantName ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    try {
      setIsUpdating(true);

      await updateTenantDetails({
        variables: {
          tenantId,
          name: pendingName,
          logoUrl: tenantLogoUrl,
        },
      });

      onOpenChange(false);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{t('tenantAdmin.general.editNameTitle')}</DialogTitle>
        </DialogHeader>
        <Input
          value={pendingName}
          onChange={(e) => setPendingName(e.target.value)}
          placeholder={t('tenantAdmin.general.namePlaceholder')}
          aria-label={t('tenantAdmin.general.nameInputAria')}
        />
        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            disabled={isUpdating}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={isUpdating || !pendingName || pendingName === tenantName}
          >
            {t('tenantAdmin.common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TenantNameSettings: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const { t } = useTranslation();

  return (
    <SettingsEditableValue
      value={tenantName ?? ''}
      editLabel={t('tenantAdmin.general.editName')}
      renderDialog={(props) => <EditTenantNameDialog {...props} />}
    />
  );
};
